import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { getBoundActions } from '../../../../../actions/index'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { StaticTextProvider, useTranslatedText } from '../../../../StaticText/StaticTextContext'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import { H1, H2 } from '../../../../Heading/index'
import Check from '../../../../Icon/icons/Check/index'
import ShareAndroid from '../../../../Icon/icons/ShareAndroid/index'
import PlayOutline from '../../../../Icon/icons/PlayOutline/index'
import PlaylistOutline from '../../../../Icon/icons/PlaylistOutline/index'
import Clock from '../../../../Icon/icons/Clock/index'
import EyeShow from '../../../../Icon/icons/EyeShow/index'
import User from '../../../../Icon/icons/User/index'
import Locked from '../../../../Icon/icons/Locked/index'
import FollowTag from '../../../shared/FollowTag/index'
import CircularAdd from '../../../../Icon/icons/CircularAdd/index'
import { TYPE_USER_PLAYLIST_ADD_REMOVE } from '../../../../../services/dialog/index'
import {
  computeTotalDuration,
  getFirstContent,
  getEpisodeThumbnail,
  formatEpisodeDuration,
} from '../../../../../services/community/identity/library/content-utils'
import { getPlaylistDisplayName } from '../../../../../services/community/index'
import useCopyFeedback from '../../../../../services/community/shared/useCopyFeedback'
import type { PlaylistByIdData, StaticTextMap } from '../../../../../services/community/types'

/** Number of episodes visible as "previews" for anonymous users. */
const ANON_PREVIEW_COUNT = 2

interface PlaylistSharedViewProps {
  playlist: PlaylistByIdData
  isLoggedIn: boolean
  isOwner: boolean
  isFollowing: boolean
  followerCount?: number
  onFollow?: () => void
  onUnfollow?: () => void
  onShare?: () => void
  onEpisodePlay?: (contentId: string, path: string, contentType: string) => void
  /**
   * Per-row "Preview" link in the anon view. Same payload as `onEpisodePlay`,
   * but the parent always appends `?fullplayer=feature` so the anon viewer
   * lands directly on the public preview clip rather than the marketing page.
   */
  onEpisodePreview?: (contentId: string, path: string, contentType: string) => void
  /**
   * Fires when an anon viewer clicks the "Start Free Trial" CTA in the unlock
   * panel — the share → signup virality conversion. The parent wires this to
   * `community_shared_link_signup_clicked`.
   */
  onSignupClicked?: () => void
  /**
   * Fires when a viewer clicks the playlist's curator-profile link. The
   * parent wires this to `community_shared_link_creator_clicked` (the
   * creator-discovery virality signal).
   */
  onCuratorClicked?: () => void
}

interface EpisodeRowProps {
  item: NonNullable<PlaylistByIdData['playlistItems']>[number]
  index: number
  isSelected: boolean
  isLoggedIn: boolean
  isOwner: boolean
  copiedIndex: number | null
  onCopyClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  onPlayClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  onAddToPlaylist: (contentId: string, contentType: string) => void
}

function EpisodeRowBase ({
  item,
  index,
  isSelected,
  isLoggedIn,
  isOwner,
  copiedIndex,
  onCopyClick,
  onPlayClick,
  onAddToPlaylist,
}: EpisodeRowProps) {
  const stShareEpisode = useTranslatedText({ keys: ['shareEpisode'] })
  const stPlay = useTranslatedText({ keys: ['play'] })
  const stAddToPlaylist = useTranslatedText({ keys: ['addToPlaylist'] })

  const content = getFirstContent(item)

  const handleAdd = useCallback(() => {
    if (!content) return
    const id = content.contentId
    if (id === undefined || id === null) return
    onAddToPlaylist(String(id), content.contentType || 'video')
  }, [onAddToPlaylist, content])

  if (!content) return null
  const thumbnail = getEpisodeThumbnail(content)
  const duration = content.featureMedia?.duration
  const durationStr = duration ? formatEpisodeDuration(duration) : ''
  const seasonEp = content.seasonNumber && content.episodeNumber
    ? `S${content.seasonNumber}:Ep${content.episodeNumber}`
    : ''

  return (
    <div
      className={`playlist-shared-view__episode-row${isSelected ? ' playlist-shared-view__episode-row--active' : ''}`}
    >
      <span className="playlist-shared-view__episode-number">
        {index + 1}
      </span>
      {thumbnail ? (
        <div className="playlist-shared-view__episode-thumb">
          <img src={thumbnail} alt={content.title || ''} loading="lazy" />
        </div>
      ) : null}
      <div className="playlist-shared-view__episode-info">
        <span className="playlist-shared-view__episode-title">
          {content.title || ''}
        </span>
        {seasonEp && (
          <span className="playlist-shared-view__episode-season">
            {seasonEp}
          </span>
        )}
      </div>
      {durationStr ? (
        <span className="playlist-shared-view__episode-duration">
          {durationStr}
        </span>
      ) : null}
      <div className="playlist-shared-view__episode-actions">
        {isLoggedIn && (
          <>
            {!isOwner && content.path && (
              <Button
                type={BUTTON_TYPES.GHOST}
                className="playlist-shared-view__episode-action-btn"
                aria-label={stShareEpisode}
                onClick={onCopyClick}
                data-index={index}
                data-path={content.path}
              >
                {copiedIndex === index
                  ? <Check />
                  : <ShareAndroid />}
              </Button>
            )}
            <Button
              type={BUTTON_TYPES.GHOST}
              className="playlist-shared-view__episode-add"
              aria-label={stAddToPlaylist}
              onClick={handleAdd}
            >
              <CircularAdd size={40} />
            </Button>
          </>
        )}
        {content.path ? (
          <Button
            type={BUTTON_TYPES.GHOST}
            className="playlist-shared-view__episode-play"
            aria-label={stPlay}
            onClick={onPlayClick}
            data-index={index}
            data-contentid={content.contentId}
            data-path={content.path}
            data-contenttype={content.contentType || 'video'}
          >
            <PlayOutline />
          </Button>
        ) : null}
      </div>
    </div>
  )
}

const EpisodeRow = React.memo(EpisodeRowBase)

function PlaylistSharedViewInner ({
  playlist,
  isLoggedIn,
  isOwner,
  isFollowing,
  followerCount = 0,
  onFollow,
  onUnfollow,
  onShare,
  onEpisodePlay,
  onEpisodePreview,
  onSignupClicked,
  onCuratorClicked,
}: PlaylistSharedViewProps) {
  const dispatch = useDispatch()

  const handleAddToPlaylist = useCallback((contentId: string, contentType: string) => {
    const { dialog } = getBoundActions(dispatch)
    dialog.renderModal(TYPE_USER_PLAYLIST_ADD_REMOVE, {
      contentId,
      contentType,
    })
  }, [dispatch])

  const totalDuration = useMemo(
    () => computeTotalDuration(playlist.playlistItems || []) || '0m',
    [playlist.playlistItems],
  )
  // Sort by `playlistPosition` DESC (highest = top of list) so the public
  // shared view matches the user-set order persisted by the reorder mutation.
  // Mirrors the sort applied by `enrichPlaylistItems` for owner surfaces
  // (see lib/services/community/identity/library/content-utils.ts).  The
  // server's `Playlist.playlistItems` relation has no `order` arg and returns
  // items in default DB order, so the sort has to live client-side.  Items
  // with `playlistPosition: null` keep their relative order via stable sort.
  const items = useMemo(() => {
    const raw = playlist.playlistItems || []
    return [...raw].sort((a, b) => {
      if (a.playlistPosition == null && b.playlistPosition == null) return 0
      if (a.playlistPosition == null) return 1
      if (b.playlistPosition == null) return -1
      return b.playlistPosition - a.playlistPosition
    })
  }, [playlist.playlistItems])
  const episodeCount = playlist.itemCount || items.length

  // ── Selected episode tracking ──────────────────────────────────
  const [selectedEpisodeIndex, setSelectedEpisodeIndex] = useState(0)

  // ── Per-episode share-to-clipboard state ────────────────────────
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const copiedIndexTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { handleCopy: clipboardCopy } = useCopyFeedback()

  useEffect(() => () => {
    if (copiedIndexTimerRef.current) clearTimeout(copiedIndexTimerRef.current)
  }, [])

  const handleEpisodePlayClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const btn = e.currentTarget
    const index = Number(btn.dataset.index)
    const contentId = btn.dataset.contentid || ''
    const path = btn.dataset.path || ''
    const contentType = btn.dataset.contenttype || 'video'
    setSelectedEpisodeIndex(index)
    if (onEpisodePlay && contentId && path) {
      onEpisodePlay(contentId, path, contentType)
    }
  }, [onEpisodePlay])

  const handlePreviewClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const btn = e.currentTarget
    const contentId = btn.dataset.contentid || ''
    const path = btn.dataset.path || ''
    const contentType = btn.dataset.contenttype || 'video'
    if (onEpisodePreview && path) {
      onEpisodePreview(contentId, path, contentType)
    }
  }, [onEpisodePreview])

  const handleEpisodeCopy = useCallback(async (index: number, path: string) => {
    if (typeof window === 'undefined') return
    const absolutePath = path.startsWith('/') ? path : `/${path}`
    await clipboardCopy(`${window.location.origin}${absolutePath}`)
    setCopiedIndex(index)
    if (copiedIndexTimerRef.current) clearTimeout(copiedIndexTimerRef.current)
    copiedIndexTimerRef.current = setTimeout(() => setCopiedIndex(null), 2000)
  }, [clipboardCopy])

  const handleEpisodeCopyClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const btn = e.currentTarget
    const index = Number(btn.dataset.index)
    const path = btn.dataset.path || ''
    handleEpisodeCopy(index, path)
  }, [handleEpisodeCopy])

  // curatorDisplayName / showCuratorLink / curatorAvatar are masked whenever
  // visibility is ANONYMOUS — this is the share-preview surface, so the owner
  // sees the same masked curator a non-owner viewer would see (otherwise the
  // owner can't verify their identity is actually hidden before sharing the
  // link). The owner's real name/avatar still render in other surfaces
  // (e.g. My Library) that don't use this component.
  const visibility = (playlist.visibility || '').toUpperCase()
  const isAnonymousToViewer = visibility === 'ANONYMOUS'
  const curatorUid = playlist.userProfile?.uuid || null

  const stCuratedBy = useTranslatedText({ keys: ['curatedBy'] })
  const stCuratedAndSharedBy = useTranslatedText({ keys: ['curatedAndSharedBy'] })
  const stAnonymous = useTranslatedText({ keys: ['anonymous'] })
  const stEpisodes = useTranslatedText({ keys: ['episodes'] })
  const stShare = useTranslatedText({ keys: ['share'] })
  const stStartWatching = useTranslatedText({ keys: ['startWatching'] })
  const stEpisodePreviews = useTranslatedText({ keys: ['episodePreviews'] })
  const stPreviewHint = useTranslatedText({ keys: ['previewHint'] })
  const stPreview = useTranslatedText({ keys: ['preview'] })
  const stMoreEpisodes = useTranslatedText({ keys: ['moreEpisodes'] })
  const stMembersOnly = useTranslatedText({ keys: ['membersOnly'] })
  const stUnlockTitle = useTranslatedText({ keys: ['unlockTitle'] })
  const stStartFreeTrial = useTranslatedText({ keys: ['startFreeTrial'] })
  const stLogIn = useTranslatedText({ keys: ['logIn'] })
  const stViews = useTranslatedText({ keys: ['views'] })
  const stFriendOfGaia = useTranslatedText({ keys: ['friendOfGaia'] })
  const stWatchLater = useTranslatedText({ keys: ['watchLater'] })
  const stFavorites = useTranslatedText({ keys: ['favorites'] })
  const systemPlaylistNames = useMemo(
    () => ({ watchLater: stWatchLater, favorites: stFavorites }),
    [stWatchLater, stFavorites],
  )

  const viewCount = playlist.viewCount || 0

  // unlockDescription interpolates {count}. The hook handles `{name}` style
  // placeholders, so pass episode count via the params bag.
  const stUnlockDescription = useTranslatedText({
    keys: ['unlockDescription'],
    params: { count: String(episodeCount) },
  })

  // The curator name falls back to a localized "Friend of Gaia" when the
  // backend supplies neither a display name nor anonymized visibility.
  const curatorNameForDisplay = playlist.userProfile?.displayName
    || stFriendOfGaia
  const resolvedCuratorDisplayName = curatorNameForDisplay && !isAnonymousToViewer
    ? curatorNameForDisplay
    : ''
  const showCuratorLink = !!curatorUid && !!resolvedCuratorDisplayName && !isAnonymousToViewer

  // ── Anonymous view: split items into preview + locked ──────────
  const previewItems = !isLoggedIn ? items.slice(0, ANON_PREVIEW_COUNT) : items
  const lockedItems = !isLoggedIn ? items.slice(ANON_PREVIEW_COUNT) : []
  const heroThumbnail = useMemo(() => {
    if (items.length === 0) return ''
    return getEpisodeThumbnail(getFirstContent(items[0]))
  }, [items])
  const curatorAvatar = isAnonymousToViewer
    ? ''
    : (playlist.userProfile?.avatar?.card || '')

  const handleStartWatching = useCallback(() => {
    if (items.length === 0) return
    const firstContent = getFirstContent(items[0])
    if (firstContent?.path && onEpisodePlay) {
      onEpisodePlay(
        String(firstContent.contentId || ''),
        firstContent.path,
        firstContent.contentType || 'video',
      )
    }
  }, [items, onEpisodePlay])

  // ── Anonymous/non-member view ──────────────────────────────────
  if (!isLoggedIn) {
    return (
      <div className="playlist-shared-view playlist-shared-view--anon">
        {/* Hero banner */}
        <div
          className="playlist-shared-view__hero"
          style={heroThumbnail ? { backgroundImage: `url(${heroThumbnail})` } : undefined}
        >
          <div className="playlist-shared-view__hero-overlay">
            <H1 className="playlist-shared-view__hero-title">
              {getPlaylistDisplayName(playlist, systemPlaylistNames)}
            </H1>
            <p className="playlist-shared-view__hero-curator">
              {stCuratedBy}
              {' '}
              <strong>{resolvedCuratorDisplayName || stAnonymous}</strong>
            </p>
            {playlist.description && (
              <p className="playlist-shared-view__hero-description">
                {playlist.description}
              </p>
            )}
            <div className="playlist-shared-view__hero-stats">
              <span className="playlist-shared-view__hero-stat">
                <PlaylistOutline />
                {`${episodeCount} ${stEpisodes.toLowerCase()}`}
              </span>
              <span className="playlist-shared-view__hero-stat">
                <Clock />
                {totalDuration}
              </span>
              {viewCount > 0 && (
                <span className="playlist-shared-view__hero-stat">
                  <EyeShow />
                  {`${viewCount} ${stViews}`}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="detail-page-layout-template">
          <div className="detail-page-layout-template__content">
            <div className="detail-page-layout-template__center">
              {/* Curator row + Start Watching CTA */}
              <div className="playlist-shared-view__curator-row">
                <div className="playlist-shared-view__curator-info">
                  {curatorAvatar ? (
                    <img
                      className="playlist-shared-view__curator-avatar"
                      src={curatorAvatar}
                      alt=""
                    />
                  ) : (
                    <span className="playlist-shared-view__curator-avatar-placeholder">
                      {(resolvedCuratorDisplayName || stAnonymous).charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="playlist-shared-view__curator-text">
                    <span className="playlist-shared-view__curator-label">
                      {stCuratedAndSharedBy}
                    </span>
                    <strong className="playlist-shared-view__curator-name">
                      {showCuratorLink ? <a href={`/profile/${curatorUid}`} onClick={onCuratorClicked}>{resolvedCuratorDisplayName}</a> : (resolvedCuratorDisplayName || stAnonymous)}
                    </strong>
                  </div>
                </div>
                <Button
                  type={BUTTON_TYPES.PRIMARY}
                  className="playlist-shared-view__start-watching-btn"
                  onClick={handleStartWatching}
                >
                  <PlayOutline />
                  {stStartWatching}
                </Button>
              </div>

              {/* Episode previews */}
              <div className="playlist-shared-view__episodes">
                <H2 className="playlist-shared-view__episodes-header">
                  <span className="playlist-shared-view__episodes-label">
                    {stEpisodePreviews}
                  </span>
                </H2>
                <p className="playlist-shared-view__preview-hint">{stPreviewHint}</p>

                <div className="playlist-shared-view__episode-list">
                  {previewItems.map((item, index) => {
                    const content = getFirstContent(item)
                    if (!content) return null
                    const thumbnail = getEpisodeThumbnail(content)
                    const durationStr = content.featureMedia?.duration
                      ? formatEpisodeDuration(content.featureMedia.duration)
                      : ''
                    return (
                      <div
                        key={item.id || `ep-${index}`}
                        className="playlist-shared-view__episode-row"
                      >
                        <span className="playlist-shared-view__episode-number">
                          {index + 1}
                        </span>
                        {thumbnail ? (
                          <div className="playlist-shared-view__episode-thumb">
                            <img src={thumbnail} alt={content.title || ''} loading="lazy" />
                          </div>
                        ) : null}
                        <div className="playlist-shared-view__episode-info">
                          <span className="playlist-shared-view__episode-title">
                            {content.title || ''}
                          </span>
                          {durationStr && (
                            <span className="playlist-shared-view__episode-duration-inline">
                              {durationStr}
                            </span>
                          )}
                        </div>
                        <Button
                          type={BUTTON_TYPES.LINK}
                          className="playlist-shared-view__preview-link"
                          data-contentid={String(content.contentId || '')}
                          data-path={content.path || ''}
                          data-contenttype={content.contentType || 'video'}
                          onClick={handlePreviewClick}
                        >
                          {stPreview}
                        </Button>
                      </div>
                    )
                  })}
                </div>

                {/* Locked episodes divider */}
                {lockedItems.length > 0 && (
                  <>
                    <div className="playlist-shared-view__locked-divider">
                      <Locked />
                      <span className="playlist-shared-view__locked-count">
                        {`${lockedItems.length} ${stMoreEpisodes}`}
                      </span>
                      <span className="playlist-shared-view__members-only-pill">
                        {stMembersOnly}
                      </span>
                    </div>

                    <div className="playlist-shared-view__episode-list playlist-shared-view__episode-list--locked">
                      {lockedItems.map((item, index) => {
                        const content = getFirstContent(item)
                        if (!content) return null
                        const thumbnail = getEpisodeThumbnail(content)
                        const durationStr = content.featureMedia?.duration
                          ? formatEpisodeDuration(content.featureMedia.duration)
                          : ''
                        return (
                          <div
                            key={item.id || `locked-${index}`}
                            className="playlist-shared-view__episode-row playlist-shared-view__episode-row--locked"
                          >
                            <span className="playlist-shared-view__episode-number">
                              {ANON_PREVIEW_COUNT + index + 1}
                            </span>
                            {thumbnail ? (
                              <div className="playlist-shared-view__episode-thumb">
                                <img src={thumbnail} alt={content.title || ''} loading="lazy" />
                                <div className="playlist-shared-view__thumb-lock">
                                  <Locked />
                                </div>
                              </div>
                            ) : null}
                            <div className="playlist-shared-view__episode-info">
                              <span className="playlist-shared-view__episode-title">
                                {content.title || ''}
                              </span>
                              {durationStr && (
                                <span className="playlist-shared-view__episode-duration-inline">
                                  {durationStr}
                                </span>
                              )}
                            </div>
                            <span className="playlist-shared-view__members-only-link">
                              <Locked />
                              {stMembersOnly}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Bottom CTA: Unlock the full playlist */}
              <div className="playlist-shared-view__unlock-cta">
                <div className="playlist-shared-view__unlock-logo">G</div>
                <H2 className="playlist-shared-view__unlock-title">{stUnlockTitle}</H2>
                <p className="playlist-shared-view__unlock-description">
                  {stUnlockDescription}
                </p>
                <div className="playlist-shared-view__unlock-actions">
                  <Button
                    type={BUTTON_TYPES.PRIMARY}
                    className="playlist-shared-view__unlock-trial-btn"
                    url="/account"
                    onClick={onSignupClicked}
                  >
                    {stStartFreeTrial}
                  </Button>
                  <Button
                    type={BUTTON_TYPES.SECONDARY}
                    className="playlist-shared-view__unlock-login-btn"
                    url="/login"
                  >
                    {stLogIn}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── Member / owner view ───────────────────────────────────────
  return (
    <div className="playlist-shared-view">
      <div className="detail-page-layout-template">
        <div className="detail-page-layout-template__content">
          <div className="detail-page-layout-template__center">
            {/* Info section */}
            <div className="playlist-shared-view__info">
              <div className="playlist-shared-view__title-row">
                <H1 className="playlist-shared-view__title">
                  {getPlaylistDisplayName(playlist, systemPlaylistNames)}
                </H1>
                <Button
                  type={BUTTON_TYPES.GHOST}
                  className="playlist-shared-view__share-btn"
                  aria-label={stShare}
                  onClick={onShare}
                >
                  <ShareAndroid />
                </Button>
              </div>
              <p className="playlist-shared-view__curator">
                {stCuratedBy}
                {' '}
                {showCuratorLink ? <a href={`/profile/${curatorUid}`} onClick={onCuratorClicked}><strong>{resolvedCuratorDisplayName}</strong></a> : <strong>{resolvedCuratorDisplayName || stAnonymous}</strong>}
              </p>
              {playlist.description && (
                <p className="playlist-shared-view__description">
                  {playlist.description}
                </p>
              )}
            </div>

            {/* Stats row */}
            <div className="playlist-shared-view__stats-row">
              <span className="playlist-shared-view__stat">
                <PlaylistOutline />
                {`${episodeCount} ${stEpisodes.toLowerCase()}`}
              </span>
              <span className="playlist-shared-view__stat">
                <Clock />
                {totalDuration}
              </span>
              {viewCount > 0 && (
                <span className="playlist-shared-view__stat">
                  <EyeShow />
                  {`${viewCount} ${stViews}`}
                </span>
              )}
              {!isOwner && (
                <span className="playlist-shared-view__follow-tag--stats">
                  <FollowTag
                    isFollowing={isFollowing}
                    followerCount={followerCount}
                    onClick={onFollow}
                    onUnfollow={onUnfollow}
                    disabled={!isLoggedIn}
                  />
                </span>
              )}
              {isOwner && followerCount > 0 && (
                <span className="playlist-shared-view__follower-count">
                  <User />
                  {followerCount}
                </span>
              )}
            </div>

            {/* Episode list */}
            <div className="playlist-shared-view__episodes">
              <div className="playlist-shared-view__episodes-header-row">
                <H2 className="playlist-shared-view__episodes-header">
                  <span className="playlist-shared-view__episodes-label">{stEpisodes}</span>
                  {' '}
                  <span className="playlist-shared-view__episodes-count">{items.length}</span>
                </H2>
                {!isOwner && (
                  <span className="playlist-shared-view__follow-tag--header">
                    <FollowTag
                      isFollowing={isFollowing}
                      followerCount={followerCount}
                      onClick={onFollow}
                      onUnfollow={onUnfollow}
                      disabled={!isLoggedIn}
                    />
                  </span>
                )}
              </div>
              <div className="playlist-shared-view__episode-list">
                {items.map((item, index) => (
                  <EpisodeRow
                    key={item.id || `ep-${index}`}
                    item={item}
                    index={index}
                    isSelected={index === selectedEpisodeIndex}
                    isLoggedIn={isLoggedIn}
                    isOwner={isOwner}
                    copiedIndex={copiedIndex}
                    onCopyClick={handleEpisodeCopyClick}
                    onPlayClick={handleEpisodePlayClick}
                    onAddToPlaylist={handleAddToPlaylist}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PlaylistSharedView (props: PlaylistSharedViewProps & { staticText?: StaticTextMap }) {
  const { staticText, ...rest } = props
  return (
    <StaticTextProvider staticText={staticText}>
      <PlaylistSharedViewInner {...rest} />
    </StaticTextProvider>
  )
}

export { PlaylistSharedView }

export default connectStaticText({ storeKey: 'playlistSharedView' })(PlaylistSharedView)
